import "./SMSVerification.scss";
import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import Loader from "../../ui/Loader/Loader";
import { Alert } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { checkPhoneNumber, sendSMS, verifyCode } from "../../../../src/store/account/accountActions";

const SMSVerification = () => {
    const { t } = useTranslation("global");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [errors, setErrors] = useState({
        phoneNumber: false,
        code: false,
    });
    const [loading, setLoading] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [codeRetryTimeout, setCodeRetryTimeout] = useState(0);
    const [screen, setScreen] = useState("phone");
    const [phoneExists, setPhoneExists] = useState(false);
    const inputRefs = useRef([]);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        let timer;
        if (codeRetryTimeout > 0 && screen === "code") {
            timer = setTimeout(() => setCodeRetryTimeout(codeRetryTimeout - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [codeRetryTimeout, screen]);

    const formatPhoneNumber = (phone) => {
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.startsWith('0') && cleaned.length === 10) {
            return `+996${cleaned.substring(1)}`;
        }
        if (cleaned.startsWith('996') && cleaned.length === 12) {
            return `+${cleaned}`;
        }
        if (cleaned.length === 9) {
            return `+996${cleaned}`;
        }
        return `+996${cleaned}`;
    };

    const validatePhoneNumber = (phone) => {
        const formatted = formatPhoneNumber(phone);
        const regex = /^\+996\d{9}$/;
        return regex.test(formatted);
    };

    const checkPhone = async (phone) => {
        try {
            const formattedPhone = formatPhoneNumber(phone);
            const result = await dispatch(checkPhoneNumber({ phone_number: formattedPhone })).unwrap();
            return { exists: false, formattedPhone };
        } catch (error) {
            return {
                exists: true,
                message: error.payload || t("verification.phoneExists"),
                formattedPhone: formatPhoneNumber(phone)
            };
        }
    };

    const handleSendSMS = async (e) => {
        e.preventDefault();

        if (!phoneNumber) {
            setErrors({ ...errors, phoneNumber: true });
            setErrorMessage(t("verification.enterPhone"));
            return;
        }

        setLoading(true);

        const checkResult = await checkPhone(phoneNumber);

        if (checkResult.exists) {
            setPhoneExists(true);
            setErrorMessage(checkResult.message);
            setLoading(false);
            return;
        }

        try {
            await dispatch(sendSMS({ phone_number: checkResult.formattedPhone })).unwrap();
            setShowSuccessAlert(true);
            setErrorMessage(null);
            setPhoneExists(false);
            setScreen("code");
            setCodeRetryTimeout(60);
            setPhoneNumber(checkResult.formattedPhone);
        } catch (error) {
            console.error("SMS Error:", error);
            setErrorMessage(error.message || t("verification.tooManyRequests"));
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        const fullCode = code.join("");
        if (fullCode.length !== 6) {
            setErrors({ ...errors, code: true });
            setErrorMessage(t("verification.enterCode"));
            return;
        }

        setLoading(true);
        try {
            await dispatch(verifyCode({ code: fullCode })).unwrap();
            setShowSuccessAlert(true);
            setErrorMessage(null);
            navigate("/verified");
        } catch (error) {
            console.error("Verify Code Error:", error);
            const errorMsg = error.payload || error.message || t("verification.verifyFailed");
            setErrorMessage(
                errorMsg.toLowerCase().includes("invalid code")
                    ? t("verification.invalidCode")
                    : errorMsg
            );
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (codeRetryTimeout > 0) return;

        setLoading(true);
        try {
            await dispatch(sendSMS({ phone_number: phoneNumber })).unwrap();
            setShowSuccessAlert(true);
            setErrorMessage(null);
            setCodeRetryTimeout(60);
        } catch (error) {
            setErrorMessage(error.message || t("verification.errorResendingSMS"));
        } finally {
            setLoading(false);
        }
    };

    const handleBackToPhone = () => {
        setScreen("phone");
        setCode(["", "", "", "", "", ""]);
        setErrorMessage(null);
        setPhoneExists(false);
    };

    const handleAlertClose = () => {
        setShowSuccessAlert(false);
    };

    const handleCodeChange = (index, value) => {
        if (!/^\d?$/.test(value)) return;
    
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setErrors({ ...errors, code: false });
    
        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    return (
        <>
            {showSuccessAlert && (
                <Alert
                    message={t("verification.smsSentSuccess")}
                    type="success"
                    closable
                    onClose={handleAlertClose}
                />
            )}
            {errorMessage && (
                <Alert message={errorMessage} type="error" closable />
            )}
            {loading ? (
                <Loader />
            ) : (
                <div className="sms-verify container">
                    <h3 className="sms-verify__title">{t("verification.smsTitle")}</h3>

                    {screen === "phone" ? (
                        <div className="sms-verify__phone phone">
                            <h4>{t("verification.enterPhone")}</h4>
                            <input
                                type="tel"
                                placeholder="+996555666777 или 0555666777"
                                value={phoneNumber}
                                onChange={(e) => {
                                    setPhoneNumber(e.target.value);
                                    setErrors({ ...errors, phoneNumber: false });
                                    setErrorMessage(null);
                                    setPhoneExists(false);
                                }}
                                className={errors.phoneNumber ? "phone__input phone__input--error" : "phone__input"}
                            />
                            {phoneExists && (
                                <p className="error-message">{t("verification.phoneExists")}</p>
                            )}
                            <div className="phone__actions">
                                <button
                                    className="sms-verify__btn"
                                    onClick={handleSendSMS}
                                    disabled={!phoneNumber || loading}
                                >
                                    {t("verification.sendSMS")}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="sms-verify__code code">
                            <h4>{t("verification.enterCode")}</h4>
                            <p className="code__phone-info">
                                {t("verification.codeSentTo")} {phoneNumber}
                            </p>
                            <div className="code__inputs">
                                {code.map((digit, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        maxLength="1"
                                        value={digit}
                                        onChange={(e) => handleCodeChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        className={errors.code ? "code__input code__input--error" : "code__input"}
                                    />
                                ))}
                            </div>
                            <button
                                className="sms-verify__btn"
                                onClick={handleVerifyCode}
                                disabled={code.join("").length !== 6 || loading}
                            >
                                {t("verification.verifyCode")}
                            </button>

                            <div className="code__resend">
                                {codeRetryTimeout > 0 ? (
                                    <p>
                                        {t("verification.resendIn")} {codeRetryTimeout} {t("verification.seconds")}
                                    </p>
                                ) : (
                                    <button
                                        className="sms-verify__btn"
                                        onClick={handleResendCode}
                                        disabled={loading}
                                    >
                                        {t("verification.resendCode")}
                                    </button>
                                )}
                            </div>

                            <button
                                className="sms-verify__btn"
                                onClick={handleBackToPhone}
                                disabled={loading}
                            >
                                {t("verification.backToPhone")}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default SMSVerification;